// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Databases
//  ---------------------------------------------------------------------------
//  The room the Main DB lives in, and the door to every database beside it.
//
//  ── A CATALOGUE SCREEN, THEN A DATABASE WINDOW ────────────────────────────
//  The list is a child screen of Tabs and therefore lives inside the day panel.
//  Choosing a database leaves that catalogue mounted and opens the database's
//  real window in the workspace. That distinction is architectural: the list
//  helps you choose a place; the database is the place where work happens.
//
//  ── THE MAIN DB IS FIRST, AND IT IS FIRST IN THE ENGINE ───────────────────
//  Nothing here sorts it to the top. `mdDatabaseList` already returns the
//  account's own catalogue ahead of everything alphabetical, which is what lets
//  the picker, this list and the Organizer agree about what comes first without
//  three copies of the rule.
//
//  ── WHAT A ROW SAYS IT HOLDS ──────────────────────────────────────────────
//  The Main DB counts in RECORDS; a database somebody made counts in ROWS. Not
//  a synonym: a row in the Main DB is a record kept in its own home somewhere
//  else, and calling those rows would quietly promise you could delete one from
//  here. The count is the real row set for the active view, virtual members
//  included, so the number on the door matches the number behind it.
//
//  ── THE SELECTED DATABASE IS A WINDOW, AND OPENS ON THE BOARD ─────────────
//  A selected database opens over the BOARD and takes exactly the board: the
//  catalogue stays readable beside it, and the window is as wide as this screen
//  has room for without covering the panel.
//  Full screen is a keystroke away and is where the real cataloguing happens;
//  it is the second size, not the first, because a catalogue that takes the
//  whole screen to answer one question costs you your place every time you ask.
//
//  Two sizes, and the small one exists only where there is somewhere to float:
//  the DOCKED-CANVAS desktop. A phone has no board, and an upright iPad gives
//  its whole viewport to the board rather than docking a panel beside it — over
//  there "the board" and "the screen" are the same rectangle, so the second size
//  would be the first one wearing a margin. Both get full screen and no control,
//  which is why the size is asked of useMdLayout rather than remembered: rotate
//  an iPad and the window must already be right, not right after the next open.
//
//  It PORTALS to document.body. The panel clips its own children, so a window
//  meant to sit over the board cannot be born inside the panel — same escape
//  DbFullScreen makes, one layer below it (140 < 150), so a database's own
//  full-screen and its row peeks still open on top of this window.
// ════════════════════════════════════════════════════════════════════════════

const { useState: useStateDBS, useMemo: useMemoDBS, useEffect: useEffectDBS,
  useRef: useRefDBS } = React;
const { ink: inkDBS, useMdDismissLayer: useEscapeDBS, MDI: MDIDBS, mdIsLight: isLightDBS,
  useMdLayout: useLayoutDBS } = window;

/* The window itself. `full` is the viewport; `board` is the board's own rectangle
   — the day panel's trailing edge out to the far edge of the screen — so the
   window is as wide as the screen allows and never a pixel over the day. It is
   measured off `L.panel` rather than given a width of its own: the panel's width
   is md-layout's to decide, and a second copy of 420 sitting here would become a
   silent overlap the day that number moves.

   The gutter is deliberate and is what keeps this a WINDOW. Flush to the board
   it would read as a third docked column — permanent, yours to live with. The
   board showing around it says this is over your work, and closes. */
const DBS_GUTTER = 22;

/* Published, because the Workshop is the SAME WINDOW opened on a different
   catalogue and a second copy of these numbers would be a silent overlap the
   day either of them moved. The gutter, the two insets, the corner radius and
   the spring are one rectangle with two tenants. (W-5) */
function dbsWindowStyle(size, light, panel, resizable) {
  /* Where there is no second size there is no window either — the catalogue IS
     the screen, so it goes edge to edge with square corners and no shadow to
     cast onto nothing. A gutter here would be a margin around the only thing
     on screen. */
  if (!resizable) return { inset: 0, borderRadius: 0,
    /* THE GLASS STAYS EDGE TO EDGE, THE WRITING MOVES. Full screen means full
       screen — the surface is supposed to reach the corners of the device, and
       on iPadOS the app's own black runs under a translucent status bar by
       design (scripts/build.mjs asks for black-translucent). What must not run
       under it is anything you read or press, and until 2026-08-18 everything
       did: Marwan's upright iPad drew `Projects · All Pursuits · +` beneath the
       clock. Padding on the border box moves every child at once, so the title
       row, the tabs and the body clear the bar without any of them being told.
       Unfloored: a window with no resting gutter has nothing to protect, and a
       floor here would be padding this window never had. */
    boxSizing: 'border-box',
    paddingTop: 'var(--md-safe-top)',
    paddingBottom: 'var(--md-safe-bottom)',
    paddingInline: 'var(--md-safe-inline)',
    animation: 'mdWhenIn .26s cubic-bezier(.22,1,.36,1)' };
  /* One vertical rectangle for the split workspace. The side panel already
     owns the correct desktop rest and the device-aware iPad rest through these
     two tokens; a separate 22px window gutter made the workspace shorter at
     both ends. Sharing the tokens makes their top and bottom edges identical
     while still letting the system status/home areas win over the 14px floor. */
  const gutterBlockStart = 'var(--md-top-inset)';
  const gutterBlockEnd = 'var(--md-bottom-inset)';
  /* One number for both inline edges — the token is deliberately side-agnostic,
     because env() is physical and this rectangle is written logically. */
  const gutterInline = `max(var(--md-safe-inline), ${DBS_GUTTER}px)`;
  return {
    /* Logical on all four sides. The block pair is the side panel's pair; the
       inline pair keeps the workspace's existing 22px breathing room. */
    insetBlockStart: gutterBlockStart, insetBlockEnd: gutterBlockEnd,
    insetInlineEnd: gutterInline,
    /* The only difference between the two sizes: `full` starts at the screen's
       edge, `board` starts after the day. Same gutter either way, so growing the
       window reads as the same window growing and not as a different sheet. The
       day's width is ADDED to the gutter rather than taken out of it: a window
       that spent its gutter on an inset would sit on the panel it stands beside. */
    insetInlineStart: size === 'full' ? gutterInline : `calc(${gutterInline} + ${panel}px)`,
    borderRadius: 'var(--md-radius-sheet)',
    border: `0.75px solid ${inkDBS(light ? 0.1 : 0.14)}`,
    boxShadow: light ? '0 24px 70px rgba(20,24,34,.20)' : '0 26px 80px rgba(0,0,0,.52)',
    animation: 'mdDbSpring .34s cubic-bezier(.22,.8,.25,1)',
  };
}

/* `initial` is the Finder's door: {id, row} opens the space already standing
   inside that database, with the row peeked. The caller keys the mount on it,
   so arriving somewhere else is a fresh room, not a transition. */
function DatabaseSpace({ accent, onClose, initial = null, closeInitialToParent = false,
  onOpenDatabase, onWindowOpenChange, catalogueStanding = null, onCatalogueClose = null }) {
  const light = isLightDBS();
  const L = useLayoutDBS();
  const [openId, setOpenId] = useStateDBS(initial ? initial.id : null);
  /* A database reached directly from Search/Recents has no catalogue in its
     route. A database chosen from the catalogue keeps that catalogue mounted
     until the person closes it; closing one layer must never tear down the
     other merely because this component coordinates both portals.
     `catalogueStanding` hands that fact to the scene (N-9): when the App
     provides it, the stack decides whether the catalogue shows and this
     component's own state stops voting — a close it performs itself goes
     through `onCatalogueClose`, so the entry leaves the stack too. */
  const [catalogueLocal, setCatalogueLocal] = useStateDBS(!initial);
  const catalogueOpen = catalogueStanding === null ? catalogueLocal : catalogueStanding;
  const setCatalogueOpen = setCatalogueLocal;
  /* `L.canvas && !L.overlay` is exactly the docked-canvas desktop: canvas is true
     for an upright iPad too, and that one is full-screen-only. Asked here rather
     than stored, so the answer changes with the window and not with the state. */
  const resizable = !!L.canvas && !L.overlay;
  /* HOW YOU LEFT IT (CS-4). This used to be useState('board'), which threw your
     choice away the moment the window closed. The device still decides the
     default; the hook hands back your last explicit choice on THIS shape of
     screen, and its setter is the write, so there is no second call to forget.
     Where there is no second size there is nothing to remember either, hence
     the one-item list — a remembered `board` cannot come back on a phone. */
  const [wanted, setWanted] = window.useMdRemembered('databases', L,
    resizable ? window.MD_SPACE_SIZES : window.MD_SPACE_FULL_ONLY,
    resizable ? 'board' : 'full');
  const size = resizable ? wanted : 'full';

  /* The App seats the window on the scene by the id it announces here, so the
     announcement is the id itself, not a boolean — `null` and `false` both
     read as closed on the other side. */
  useEffectDBS(() => {
    if (onWindowOpenChange) onWindowOpenChange(openId);
    return () => { if (onWindowOpenChange) onWindowOpenChange(false); };
  }, [openId, onWindowOpenChange]);

  const closeCatalogue = () => {
    if (openId === null) { onClose(); return; }
    if (onCatalogueClose) onCatalogueClose(); else setCatalogueOpen(false);
    /* The database remains a native workspace destination, so restored Tabs
       stands beside it rather than becoming modal over it. */
    window.dispatchEvent(new CustomEvent('mdtabsshow', { detail: { sideDestination: true } }));
  };

  /* The database owns the first Escape while it is open. This handler is only
     armed for a visible catalogue; after that catalogue closes, Escape cannot
     accidentally call the parent and dismantle the surviving window. */
  useEscapeDBS(closeCatalogue, catalogueOpen);

  /* Clicking Databases in the restored Tabs reopens this already-mounted
     catalogue. A small event is preferable to remounting the coordinator and
     losing the selected database's live window state. */
  useEffectDBS(() => {
    const reopen = () => setCatalogueOpen(true);
    window.addEventListener('mddatabasescatalogueopen', reopen);
    return () => window.removeEventListener('mddatabasescatalogueopen', reopen);
  }, []);

  /* THE SCENE ASKS, THE SPACE ANSWERS (N-9). When a journal walk retires the
     window's entry, the App cannot reach into this component's state — it
     dispatches `retire` and this owner closes the same way its own × would.
     `restore` is the Forward walk onto that entry: the scene already holds
     the window again, and the space stands it back up by id. */
  const dbwRef = useRefDBS(null);
  dbwRef.current = { catalogueOpen, closeInitialToParent, onClose };
  useEffectDBS(() => {
    const retire = () => {
      const now = dbwRef.current;
      if (now.closeInitialToParent || !now.catalogueOpen) now.onClose();
      else setOpenId(null);
    };
    const restore = (event) => {
      if (event.detail && event.detail.id) setOpenId(event.detail.id);
    };
    window.addEventListener('mddbwindowretire', retire);
    window.addEventListener('mddbwindowrestore', restore);
    return () => {
      window.removeEventListener('mddbwindowretire', retire);
      window.removeEventListener('mddbwindowrestore', restore);
    };
  }, []);

  /* The names, which cost nothing: mdDatabaseList reads the documents and
     composes no rows. What each one HOLDS is a separate question and a far more
     expensive one, so it is asked separately below. */
  const rows = useMemoDBS(() => (window.mdDatabaseList ? window.mdDatabaseList() : [])
    .map((db) => ({
      db,
      id: db.id,
      name: db.name,
      builtin: window.mdDatabaseBuiltin ? window.mdDatabaseBuiltin(db.id) : false,
    })), [openId]);

  /* ── the counting happens behind the open door ────────────────────────────
     Counting the Main DB means composing every record on the account, because
     the number under its name has to be the number behind it — and on a large
     account that is half a second. Spent during render it was half a second of
     nothing on screen, to answer a question about a list the user could not yet
     see. So it moves to an effect: React commits the names, the browser paints,
     and the numbers land on the next frame.

     Not an estimate and not a sample — the same count, off the critical path.
     The cheap databases finish inside one frame and nobody sees a change; the
     expensive one is the only place the two frames are visible, which is
     exactly where blocking was worst.

     A COUNT, NOT A SORTED PAGE. Asking for the rows here sorted every one of
     them to print a number: five thousand comparisons for the word "5000".
     Filters change a count; sorting cannot.

     No revision guard, deliberately: mdDatabaseRowCount is itself memoised on
     mdStorageRevision(), so re-running this after a close-and-reopen with
     nothing written costs a map lookup per database. A second cache here would
     be a second thing to get wrong about when it goes stale. */
  const [counts, setCounts] = useStateDBS(null);
  useEffectDBS(() => {
    const next = {};
    for (const row of rows) {
      next[row.id] = window.mdDatabaseRowCount ? window.mdDatabaseRowCount(row.db)
        : (window.mdDatabaseRowsForView ? window.mdDatabaseRowsForView(row.db).length : 0);
    }
    setCounts(next);
  }, [rows]);

  /* Before the count lands there is still a line here, and for the Main DB it
     still says what the Main DB is — that much is true before anyone has
     counted. A row never renders without this line, so nothing moves when the
     numbers arrive. */
  const held = (row) => {
    const n = counts ? counts[row.id] : undefined;
    if (row.builtin) {
      return n === undefined ? 'Every record on this account'
        : `Every record on this account · ${n} ${n === 1 ? 'record' : 'records'}`;
    }
    /* Non-breaking, because an ordinary space collapses to nothing and the
       line would have no height to hold — which is the row jumping this
       reserved line exists to prevent. */
    if (n === undefined) return ' ';
    return n === 0 ? 'Nothing in it yet' : `${n} ${n === 1 ? 'row' : 'rows'}`;
  };

  const panelHost = L.phone ? document.body
    : (document.getElementById('md-day-sidebar') || document.body);
  const catalogue = (
    <div data-md-database-catalogue data-md-panel-screen="atomic"
      data-md-phone-document-screen={L.phone ? 'true' : undefined}
      style={{ position: 'absolute', inset: 0, zIndex: 90, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', color: inkDBS(.92),
        ...(window.mdFrostDress ? window.mdFrostDress(light) : {}),
        paddingTop: 'var(--md-safe-top)', paddingBottom: 'var(--md-safe-bottom)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px 8px 18px', minHeight: 48, flexShrink: 0 }}>
        <span data-md-title-icon="databases" style={{ display: 'grid', placeItems: 'center', flex: 'none' }}>
          {MDIDBS.database ? MDIDBS.database(accent, 22) : <window.DbGlyph name="table" color={accent} size={22} />}
        </span>
        <span style={{ flex: 1, color: inkDBS(1), fontSize: 19, fontWeight: 680 }}>Databases</span>
        <window.MdDismiss onClick={closeCatalogue} label="Close Databases"
          style={{ marginInlineEnd: -4 }} />
      </div>
      <div data-md-phone-document-content={L.phone ? 'true' : undefined}
        className="md-scroll" style={{ flex: 1, overflowY: 'auto', padding: '6px 16px 32px' }}>
        {rows.map((row) => (
          <button key={row.id} data-md-database-row data-md-active={openId === row.id || undefined}
            onClick={() => {
              if (onOpenDatabase) onOpenDatabase(row.id);
              setOpenId(row.id);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '11px 2px', textAlign: 'start', cursor: 'pointer',
              background: openId === row.id ? (light ? 'rgba(124,104,230,.08)' : 'rgba(124,104,230,.12)') : 'transparent',
              border: 'none', borderRadius: 8,
              borderBottom: `0.75px solid ${inkDBS(light ? 0.07 : 0.1)}` }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span data-md-database-name
                style={{ display: 'block', color: inkDBS(0.9), fontSize: 14, fontWeight: 600,
                  fontFamily: 'var(--md-body)', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
              <span style={{ display: 'block', color: inkDBS(0.4), fontSize: 11.5, marginTop: 3,
                fontFamily: 'var(--md-body)' }}>{held(row)}</span>
            </span>
            {MDIDBS.chevron ? MDIDBS.chevron(inkDBS(0.3), 14) : null}
          </button>))}
      </div>
    </div>);

  const databaseWindow = openId !== null ? (
    <div data-md-database-space data-md-size={size}
      style={{ position: 'fixed', zIndex: 140, display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        ...(window.mdFrostDress ? window.mdFrostDress(light) : {}),
        ...dbsWindowStyle(size, light, L.panel, resizable) }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <window.DatabaseSurface databaseId={openId} mode="full" accent={accent}
          windowSize={size} onWindowSize={resizable ? setWanted : null}
          initialRow={initial && initial.id === openId ? initial.row || null : null}
          onClose={closeInitialToParent || !catalogueOpen ? onClose
            /* Just the state. The window is a scene entry now: dropping the id
               dismisses it there, the journal walks one step, and the walk is
               what restores the catalogue entry's own URL — no hand-back. */
            : () => setOpenId(null)} />
      </div>
    </div>) : null;

  return <>{catalogueOpen ? ReactDOM.createPortal(catalogue, panelHost) : null}
    {databaseWindow ? ReactDOM.createPortal(databaseWindow, document.body) : null}</>;
}

Object.assign(window, { DatabaseSpace, mdSpaceWindowStyle: dbsWindowStyle, MD_SPACE_GUTTER: DBS_GUTTER });
